import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  TargetIcon, FloppyDiskIcon, PlusIcon, TrashIcon, CalendarIcon,
  TrendUpIcon, GlobeIcon, UsersThreeIcon, MapPinIcon, XIcon,
} from "@phosphor-icons/react";

/**
 * Metas de Venta — CRUD por año/mes con scope flexible (global, región, RTC).
 * Cada fila = un "scope" (un destinatario de meta). Las 12 columnas son los meses.
 * Edición inline; guardar en bulk (12 valores a la vez por scope).
 */
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function SalesTargets() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [showNewRow, setShowNewRow] = useState(false);
  const [newRow, setNewRow] = useState({ scopeType: "global", region: "", rtc_id: "", rtc_nombre: "" });
  const [rtcs, setRtcs] = useState([]);
  const [regions, setRegions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-targets/year/${year}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  // Lista de RTCs y regiones para los selectores
  const loadRefs = async () => {
    try {
      const { data } = await api.get("/representatives");
      setRtcs(Array.isArray(data) ? data : data.items || []);
    } catch (_err) { /* representatives es opcional */ }
    try {
      const { data } = await api.get("/clients");
      const items = Array.isArray(data) ? data : data.items || [];
      const reg = [...new Set(items.map((c) => c.region).filter(Boolean))].sort();
      setRegions(reg);
    } catch (_err) { /* regiones es opcional */ }
  };

  useEffect(() => { load(); }, [year]);
  useEffect(() => { loadRefs(); }, []);

  const updateMonth = (rowIdx, monthIdx, value) => {
    const v = parseFloat(value) || 0;
    setRows((prev) => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const months = [...r.months];
      months[monthIdx] = v;
      const total = months.reduce((s, x) => s + x, 0);
      return { ...r, months, total: Math.round(total * 100) / 100 };
    }));
  };

  const saveRow = async (row) => {
    const key = `${row.region}|${row.rtc_id}`;
    setSavingKey(key);
    try {
      await api.post("/sales-targets/bulk", {
        year, region: row.region || "", rtc_id: row.rtc_id || "",
        rtc_nombre: row.rtc_nombre || "", montos: row.months,
      });
      toast.success("Meta guardada");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingKey(null); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`¿Eliminar todas las metas de ${scopeLabel(row)} del año ${year}?`)) return;
    try {
      await api.post("/sales-targets/bulk", {
        year, region: row.region || "", rtc_id: row.rtc_id || "",
        rtc_nombre: row.rtc_nombre || "", montos: Array(12).fill(0),
      });
      toast.success("Metas eliminadas");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addNewRow = () => {
    const region = newRow.scopeType === "region" ? newRow.region : "";
    const rtc_id = newRow.scopeType === "rtc" ? newRow.rtc_id : "";
    const rtc_nombre = newRow.scopeType === "rtc"
      ? (rtcs.find((r) => r.id === newRow.rtc_id)?.nombre || "") : "";

    // ¿ya existe?
    const exists = rows.find((r) => (r.region || "") === region && (r.rtc_id || "") === rtc_id);
    if (exists) { toast.error("Ese scope ya tiene fila — edítalo en la tabla"); return; }

    if (newRow.scopeType === "region" && !region) { toast.error("Selecciona una región"); return; }
    if (newRow.scopeType === "rtc" && !rtc_id) { toast.error("Selecciona un RTC"); return; }

    setRows([...rows, { region, rtc_id, rtc_nombre, months: Array(12).fill(0), total: 0, ids: Array(12).fill("") }]);
    setShowNewRow(false);
    setNewRow({ scopeType: "global", region: "", rtc_id: "", rtc_nombre: "" });
    toast.success("Fila agregada — ahora carga los montos y guarda");
  };

  const totalAnualGeneral = rows.reduce((s, r) => s + r.total, 0);

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="sales-targets-page">
        {/* HEADER */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TargetIcon size={22} weight="duotone" className="text-agro-yellow" />
              <h1 className="text-2xl font-heading font-bold">Metas de Venta</h1>
            </div>
            <p className="text-sm text-text-muted">Define cuánto se espera vender por mes en HNL. Se usa en Dashboard y Reportes &quot;vs Meta&quot;.</p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              data-testid="year-selector"
              className="bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            >
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) =>
                <option key={y} value={y}>{y}</option>
              )}
            </select>
            <button
              onClick={() => setShowNewRow(true)}
              data-testid="add-scope-btn"
              className="inline-flex items-center gap-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium px-4 py-2 rounded-md text-sm"
            >
              <PlusIcon size={14} weight="bold" /> Agregar scope
            </button>
          </div>
        </div>

        {/* TOTAL ANUAL */}
        <div className="bg-card border border-border/60 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendUpIcon size={24} weight="duotone" className="text-agro-green" />
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted">Meta anual total ({year})</div>
              <div className="text-2xl font-heading font-bold text-agro-green">{fmtL(totalAnualGeneral)}</div>
            </div>
          </div>
          <div className="text-xs text-text-muted">
            {rows.length} scope{rows.length === 1 ? "" : "s"} configurado{rows.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* NUEVA FILA MODAL */}
        {showNewRow && (
          <div className="bg-card border-2 border-agro-green/40 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Nuevo scope para metas {year}</h3>
              <button aria-label="Cerrar" onClick={() => setShowNewRow(false)} className="text-text-muted hover:text-text-primary">
                <XIcon size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Tipo de scope</label>
                <select
                  value={newRow.scopeType}
                  onChange={(e) => setNewRow({ ...newRow, scopeType: e.target.value })}
                  data-testid="scope-type-select"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                >
                  <option value="global">Global (toda la empresa)</option>
                  <option value="region">Por Región</option>
                  <option value="rtc">Por RTC</option>
                </select>
              </div>
              {newRow.scopeType === "region" && (
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Región</label>
                  <input
                    list="regions-list"
                    value={newRow.region}
                    onChange={(e) => setNewRow({ ...newRow, region: e.target.value })}
                    placeholder="Ej: Zona Norte"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                    data-testid="region-input"
                  />
                  <datalist id="regions-list">
                    {regions.map((r) => <option key={r} value={r} />)}
                  </datalist>
                </div>
              )}
              {newRow.scopeType === "rtc" && (
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">RTC</label>
                  <select
                    value={newRow.rtc_id}
                    onChange={(e) => setNewRow({ ...newRow, rtc_id: e.target.value })}
                    data-testid="rtc-select"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                  >
                    <option value="">— Selecciona un RTC —</option>
                    {rtcs.map((r) => <option key={r.id} value={r.id}>{r.codigo || ""} — {r.nombre}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNewRow(false)} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
              <button onClick={addNewRow} data-testid="confirm-add-scope" className="px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Agregar</button>
            </div>
          </div>
        )}

        {/* MATRIZ DE METAS */}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="p-3 pl-5 text-left sticky left-0 bg-sidebar z-10 min-w-[200px]">Scope</th>
                  {MESES.map((m) => <th key={m} className="p-2 text-center min-w-[110px]">{m}</th>)}
                  <th className="p-3 text-right min-w-[120px]">Total año</th>
                  <th className="p-3 pr-5 text-right min-w-[110px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="15" className="p-10 text-center text-text-muted">Cargando…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="15" className="p-10 text-center text-text-muted">
                    No hay metas para {year}. Click en &quot;Agregar scope&quot; para empezar.
                  </td></tr>
                ) : rows.map((row, ri) => {
                  const key = `${row.region}|${row.rtc_id}`;
                  return (
                    <tr key={key} className="border-t border-border/40" data-testid={`row-${ri}`}>
                      <td className="p-3 pl-5 sticky left-0 bg-card z-10">
                        <ScopeLabel row={row} />
                      </td>
                      {row.months.map((m, mi) => (
                        <td key={mi} className="p-1.5">
                          <input
                            type="number"
                            value={m || ""}
                            onChange={(e) => updateMonth(ri, mi, e.target.value)}
                            placeholder="0"
                            data-testid={`meta-${ri}-${mi}`}
                            className="w-full bg-app border border-border rounded px-2 py-1.5 text-right text-xs focus:border-agro-green focus:outline-none"
                          />
                        </td>
                      ))}
                      <td className="p-3 text-right font-medium text-agro-green">{fmtL(row.total)}</td>
                      <td className="p-3 pr-5 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => saveRow(row)}
                            disabled={savingKey === key}
                            data-testid={`save-row-${ri}`}
                            className="inline-flex items-center gap-1 bg-agro-green/15 hover:bg-agro-green/25 text-agro-green border border-agro-green/30 px-3 py-1.5 rounded-md text-xs disabled:opacity-50"
                            title="Guardar la fila"
                          >
                            <FloppyDiskIcon size={12} weight="bold" /> {savingKey === key ? "..." : "Guardar"}
                          </button>
                          <button
                            onClick={() => deleteRow(row)}
                            data-testid={`del-row-${ri}`}
                            className="p-1.5 text-text-muted hover:text-red-400"
                            title="Eliminar"
                          >
                            <TrashIcon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* HELP */}
        <div className="bg-card border border-border/30 rounded-lg p-4 text-xs text-text-muted">
          <div className="flex items-start gap-2">
            <CalendarIcon size={16} className="text-agro-yellow mt-0.5" />
            <div>
              <p className="mb-1"><strong>Cómo funciona:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Cada <strong>fila</strong> es un scope distinto: meta global, meta por región, o meta por RTC.</li>
                <li>Las <strong>12 columnas</strong> son los meses del año seleccionado. Pon 0 (o vacío) si no aplica.</li>
                <li>Cambios se guardan al hacer click en <strong>&quot;Guardar&quot;</strong> de cada fila (envía los 12 valores en bulk).</li>
                <li>Si todo el año está en 0, las metas se eliminan de ese scope.</li>
                <li>El <strong>Dashboard Admin</strong> y los <strong>Reportes de Ventas</strong> usan estos datos para el &quot;vs. Meta&quot;.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ScopeLabel({ row }) {
  const isGlobal = !row.region && !row.rtc_id;
  if (isGlobal) {
    return (
      <div className="flex items-center gap-2">
        <GlobeIcon size={16} className="text-agro-green" weight="duotone" />
        <div>
          <div className="font-medium text-sm">Global</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">toda la empresa</div>
        </div>
      </div>
    );
  }
  if (row.rtc_id) {
    return (
      <div className="flex items-center gap-2">
        <UsersThreeIcon size={16} className="text-agro-yellow" weight="duotone" />
        <div>
          <div className="font-medium text-sm">{row.rtc_nombre || row.rtc_id}</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">RTC</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <MapPinIcon size={16} className="text-blue-400" weight="duotone" />
      <div>
        <div className="font-medium text-sm">{row.region}</div>
        <div className="text-[10px] text-text-muted uppercase tracking-wider">región</div>
      </div>
    </div>
  );
}

function scopeLabel(row) {
  if (!row.region && !row.rtc_id) return "Global";
  if (row.rtc_id) return `RTC ${row.rtc_nombre || row.rtc_id}`;
  return `Región ${row.region}`;
}
